import React, { useState, useMemo } from 'react';
import { Table, Btn, Modal, Field, Input, Select, SectionHeader, Badge, AlertBanner } from '../components/UI';
import { fmtM, fmtDate, todayStr, gcalUrl, exportToCSV } from '../utils';
import { showToast } from '../components/UI';

export default function Compras({ store, setItem, delItem }) {
  const { compras, movimientos } = store;
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [filtroEstado, setFiltroEstado] = useState('pendiente');
  const [payModal, setPayModal] = useState(null);
  const [payMedio, setPayMedio] = useState('transferencia');

  const hoy = todayStr();
  const proximo7 = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);

  const lista = useMemo(() => {
    return Object.values(compras || {})
      .filter(c => {
        if (filtroEstado === 'pendiente') return !c.pagado;
        if (filtroEstado === 'pagado') return c.pagado;
        return true;
      })
      .sort((a, b) => (a.fechaVenc || '').localeCompare(b.fechaVenc || ''));
  }, [compras, filtroEstado]);

  const totalPendiente = useMemo(() =>
    Object.values(compras || {}).filter(c => !c.pagado).reduce((a, c) => a + (parseFloat(c.monto) || 0), 0),
    [compras]);

  function openNew() {
    setForm({ tipo: 'cuenta', proveedor: '', monto: '', fechaVenc: '', concepto: '', medio: 'transferencia' });
    setModal('new');
  }

  function save() {
    if (!form.monto || !form.proveedor) { showToast('Completa proveedor y monto'); return; }
    const id = 'c' + Date.now();
    if (form.tipo === 'contado') {
      // Register as movement
      const movId = 'm' + Date.now();
      setItem('movimientos', movId, {
        id: movId, tipo: 'compra', fecha: form.fecha || hoy, hora: new Date().toTimeString().slice(0, 5),
        monto: parseFloat(form.monto), medio: form.medio, signo: '-', desc: form.proveedor + (form.concepto ? ' — ' + form.concepto : ''),
        proveedor: form.proveedor, autor: 'panel'
      });
      showToast('Compra de contado registrada');
    } else {
      // Register as pending account
      setItem('compras', id, {
        id, proveedor: form.proveedor, monto: parseFloat(form.monto), fechaVenc: form.fechaVenc,
        concepto: form.concepto, pagado: false, fecha: hoy
      });
      showToast('Compra a cuenta registrada');
    }
    setModal(null);
  }

  function openPay(c) {
    setPayMedio('transferencia');
    setPayModal(c);
  }

  function confirmPay() {
    const c = payModal;
    const movId = 'm' + Date.now();
    setItem('movimientos', movId, {
      id: movId, tipo: 'pago_proveedor', fecha: hoy, hora: new Date().toTimeString().slice(0, 5),
      monto: c.monto, medio: payMedio, signo: '-',
      desc: `Pago ${c.proveedor}${c.concepto ? ' — ' + c.concepto : ''}`,
      proveedor: c.proveedor, autor: 'panel'
    });
    setItem('compras', c.id, { ...c, pagado: true, fechaPago: hoy, medioPago: payMedio });
    showToast('Pago registrado en ' + (payMedio === 'efectivo' ? 'efectivo' : 'transferencia'));
    setPayModal(null);
  }

  const getEstado = (c) => {
    if (c.pagado) return { label: 'Pagado', type: 'grn' };
    if (c.fechaVenc < hoy) return { label: 'Vencido', type: 'red' };
    if (c.fechaVenc <= proximo7) return { label: 'Próximo', type: 'yel' };
    return { label: 'Pendiente', type: 'gy' };
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--rl)', padding: '14px 16px', flex: 1 }}>
          <div style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', marginBottom: 5 }}>Total pendiente de pago</div>
          <div style={{ fontSize: 24, fontWeight: 700, fontFamily: 'Courier, monospace', color: 'var(--red)' }}>{fmtM(totalPendiente)}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Btn onClick={() => exportToCSV('compras', ['Proveedor','Fecha Venc','Monto','Concepto','Estado'], Object.values(compras||{}).map(c => [c.proveedor, c.fechaVenc, c.monto, c.concepto||'', c.pagado?'Pagado':'Pendiente']))}>⬇ CSV</Btn>
          <Btn onClick={openNew} variant="grn">+ Nueva compra</Btn>
        </div>
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        {[['pendiente', 'Pendientes'], ['pagado', 'Pagadas'], ['todas', 'Todas']].map(([val, label]) => (
          <button key={val} onClick={() => setFiltroEstado(val)}
            style={{ padding: '5px 14px', borderRadius: 99, border: `1px solid ${filtroEstado === val ? 'var(--grn)' : 'var(--border2)'}`, background: filtroEstado === val ? 'var(--grn-l)' : 'var(--surf2)', color: filtroEstado === val ? 'var(--grn)' : 'var(--tx2)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            {label}
          </button>
        ))}
      </div>

      <div style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--rl)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: 'var(--surf2)' }}>
              {['Vencimiento', 'Proveedor', 'Concepto', 'Monto', 'Estado', ''].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '9px 13px', fontSize: 9.5, fontWeight: 700, color: 'var(--tx3)', borderBottom: '1px solid var(--border)', textTransform: 'uppercase' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lista.length === 0
              ? <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, color: 'var(--tx3)' }}>Sin compras en esta categoría</td></tr>
              : lista.map(c => {
                const estado = getEstado(c);
                return (
                  <tr key={c.id} style={{ borderBottom: '1px solid var(--border)', background: estado.type === 'red' ? 'var(--red-l)' : '' }}>
                    <td style={{ padding: '9px 13px', fontFamily: 'Courier, monospace', fontSize: 11, color: estado.type === 'red' ? 'var(--red)' : 'var(--tx2)' }}>
                      {fmtDate(c.fechaVenc)}
                    </td>
                    <td style={{ padding: '9px 13px', fontWeight: 600 }}>{c.proveedor}</td>
                    <td style={{ padding: '9px 13px', fontSize: 11, color: 'var(--tx3)' }}>{c.concepto || '—'}</td>
                    <td style={{ padding: '9px 13px', fontFamily: 'Courier, monospace', fontWeight: 700 }}>{fmtM(c.monto)}</td>
                    <td style={{ padding: '9px 13px' }}><Badge type={estado.type}>{estado.label}</Badge></td>
                    <td style={{ padding: '9px 13px' }}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {!c.pagado && <Btn size="sm" variant="grn" onClick={() => openPay(c)}>Pagar</Btn>}
                        {!c.pagado && c.fechaVenc && (
                          <a href={gcalUrl(`Pago ${c.proveedor}`, c.fechaVenc, `${fmtM(c.monto)}\n${c.concepto || ''}`)}
                            target="_blank" rel="noreferrer"
                            style={{ padding: '4px 7px', fontSize: 11, background: 'var(--blu-l)', color: 'var(--blu-m)', borderRadius: 6, textDecoration: 'none', fontWeight: 600 }}>📅</a>
                        )}
                        <Btn size="sm" variant="red" onClick={() => { delItem('compras', c.id); showToast('Eliminado'); }}>×</Btn>
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {modal === 'new' && (
        <Modal title="Nueva compra" onClose={() => setModal(null)} onSave={save}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 11 }}>
            <Field label="Tipo" full>
              <div style={{ display: 'flex', gap: 8 }}>
                {[['cuenta', 'A cuenta (con vencimiento)'], ['contado', 'Contado']].map(([val, label]) => (
                  <button key={val} onClick={() => setForm(f => ({ ...f, tipo: val }))}
                    style={{ flex: 1, padding: '9px', borderRadius: 8, border: `2px solid ${form.tipo === val ? 'var(--grn)' : 'var(--border2)'}`, background: form.tipo === val ? 'var(--grn-l)' : 'var(--surf2)', color: form.tipo === val ? 'var(--grn)' : 'var(--tx2)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    {label}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Proveedor">
              <Input value={form.proveedor} onChange={v => setForm(f => ({ ...f, proveedor: v }))} placeholder="Nombre del proveedor" autoFocus />
            </Field>
            <Field label="Monto">
              <Input value={form.monto} onChange={v => setForm(f => ({ ...f, monto: v }))} type="number" placeholder="0" />
            </Field>
            {form.tipo === 'cuenta' ? (
              <Field label="Fecha de vencimiento">
                <Input value={form.fechaVenc} onChange={v => setForm(f => ({ ...f, fechaVenc: v }))} type="date" />
              </Field>
            ) : (
              <>
                <Field label="Fecha">
                  <Input value={form.fecha || todayStr()} onChange={v => setForm(f => ({ ...f, fecha: v }))} type="date" />
                </Field>
                <Field label="Medio de pago">
                  <Select value={form.medio} onChange={v => setForm(f => ({ ...f, medio: v }))}>
                    <option value="efectivo">Efectivo</option>
                    <option value="transferencia">Transferencia</option>
                  </Select>
                </Field>
              </>
            )}
            <Field label="Concepto / Factura" full>
              <Input value={form.concepto} onChange={v => setForm(f => ({ ...f, concepto: v }))} placeholder="Nro de factura, descripción..." />
            </Field>
          </div>
        </Modal>
      )}

      {payModal && (
        <Modal title={`Pagar — ${payModal.proveedor}`} onClose={() => setPayModal(null)} onSave={confirmPay} saveLabel="Confirmar pago">
          <div style={{ marginBottom: 14, padding: '10px 14px', background: 'var(--surf2)', borderRadius: 8, fontSize: 12, color: 'var(--tx2)' }}>
            <strong style={{ fontFamily: 'Courier, monospace', fontSize: 15, color: 'var(--tx)' }}>{fmtM(payModal.monto)}</strong>
            {payModal.concepto && <span style={{ marginLeft: 8, color: 'var(--tx3)' }}>{payModal.concepto}</span>}
          </div>
          <Field label="Medio de pago" full>
            <div style={{ display: 'flex', gap: 8 }}>
              {[['transferencia', 'Transferencia'], ['efectivo', 'Efectivo']].map(([val, label]) => (
                <button key={val} onClick={() => setPayMedio(val)}
                  style={{ flex: 1, padding: 12, borderRadius: 8, border: `2px solid ${payMedio === val ? 'var(--grn)' : 'var(--border2)'}`, background: payMedio === val ? 'var(--grn-l)' : 'var(--surf2)', color: payMedio === val ? 'var(--grn)' : 'var(--tx2)', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                  {label}
                </button>
              ))}
            </div>
          </Field>
        </Modal>
      )}
    </div>
  );
}
